import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Save, ShieldCheck, AlertTriangle, ExternalLink } from "lucide-react";
import ArrayEditor from "@/components/ArrayEditor";

/**
 * AdminSbtPageEditor — no-code CRUD for a full page section.
 *
 * Renders three zones:
 *   1. Hero form (auto-generated from the section's `hero` keys)
 *   2. ArrayEditor per array field (pillars, team, properties, ...)
 *   3. Simple sub-form per extra object field (story, cta_banner, ...)
 *
 * Zero raw JSON exposed to the admin.
 */
export default function AdminSbtPageEditor({
  sectionKey,
  title,
  description,
  arrayFields = [],
  extraFields = [],
}) {
  const [data, setData] = useState(null);
  const [heroDraft, setHeroDraft] = useState({});
  const [arrayDrafts, setArrayDrafts] = useState({});
  const [extraDrafts, setExtraDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [origResult, setOrigResult] = useState(null);
  const [origLoading, setOrigLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/sbt-site/sections/${sectionKey}`);
      const d = r.data.data || {};
      setData(d);
      setHeroDraft(d.hero || {});
      const ad = {}, ed = {};
      for (const k of arrayFields) ad[k] = Array.isArray(d[k]) ? d[k] : [];
      for (const k of extraFields) ed[k] = (d[k] && typeof d[k] === "object") ? d[k] : {};
      setArrayDrafts(ad);
      setExtraDrafts(ed);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load section");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sectionKey]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...(data || {}), hero: heroDraft, ...arrayDrafts, ...extraDrafts };
      await api.put(`/admin/sbt-site/sections/${sectionKey}`, { data: payload });
      toast.success("Saved. Auto-publishing to live site in ~5 seconds…");
      setData(payload);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  // Flatten current draft into one plain-text blob for the plagiarism check.
  const draftText = () => {
    const collect = (node) => {
      if (typeof node === "string") return [node];
      if (Array.isArray(node)) return node.flatMap(collect);
      if (node && typeof node === "object") return Object.values(node).flatMap(collect);
      return [];
    };
    return collect({ hero: heroDraft, ...arrayDrafts, ...extraDrafts }).filter(Boolean).join(" ");
  };

  const checkOriginality = async () => {
    setOrigLoading(true);
    setOrigResult(null);
    try {
      // Send the current DRAFT text (not the saved section) so admins
      // can validate before hitting Save.
      const text = draftText();
      if (text.length < 50) {
        toast.error("Not enough content to check — add at least a paragraph first.");
        return;
      }
      const r = await api.post(`/admin/sbt-site/originality/check`, { text });
      setOrigResult(r.data);
      if (r.data.note) toast.info(r.data.note);
      else if (r.data.score === null) toast.info("Check complete — insufficient sample.");
      else if (r.data.score >= 85) toast.success(`Originality ${r.data.score}% — looks unique.`);
      else if (r.data.score >= 60) toast.warning(`Originality ${r.data.score}% — some phrases already indexed.`);
      else toast.error(`Originality ${r.data.score}% — significant overlap detected.`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Originality check failed");
    } finally { setOrigLoading(false); }
  };

  if (loading) return <div className="p-4 text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2"/>Loading {title}…</div>;

  const heroKeys = Object.keys(heroDraft || {});

  return (
    <section data-testid={`admin-${sectionKey}`}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-base font-semibold text-slate-900">{title}</h3>
          <p className="text-xs text-slate-500 mt-0.5">{description}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm" variant="outline" onClick={checkOriginality} disabled={origLoading || saving}
            data-testid={`check-originality-${sectionKey}`}
          >
            {origLoading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin"/> : <ShieldCheck className="w-3.5 h-3.5 mr-1"/>}
            Check Originality
          </Button>
          <Button size="sm" onClick={save} disabled={saving} data-testid={`save-${sectionKey}`}>
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin"/> : <Save className="w-3.5 h-3.5 mr-1"/>}
            Save Changes
          </Button>
        </div>
      </div>

      {origResult && <OriginalityPanel result={origResult} onClose={() => setOrigResult(null)} />}

      <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-3 py-1.5 mb-3">
        ⚡ Auto-publish is on — any Save automatically rebuilds the marketing site (~5s). Watch the "Publish Status" pill in the header for progress.
      </p>

      {heroKeys.length > 0 && (
        <FieldGroup title="Hero">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {heroKeys.map((k) => (
              <HeroField key={k} name={k} value={heroDraft[k]} onChange={(v) => setHeroDraft((h) => ({ ...h, [k]: v }))}/>
            ))}
          </div>
        </FieldGroup>
      )}

      {extraFields.map((k) => (
        <FieldGroup key={k} title={k.replace(/_/g, " ")}>
          <ObjectForm value={extraDrafts[k] || {}} onChange={(v) => setExtraDrafts((d) => ({ ...d, [k]: v }))}/>
        </FieldGroup>
      ))}

      {arrayFields.map((k) => (
        <ArrayEditor
          key={k}
          label={k.replace(/_/g, " ")}
          value={arrayDrafts[k] || []}
          onChange={(next) => setArrayDrafts((d) => ({ ...d, [k]: next }))}
          testIdPrefix={`${sectionKey}-${k}`}
        />
      ))}
    </section>
  );
}

function FieldGroup({ title, children }) {
  return (
    <div className="border rounded-lg bg-white p-4 mb-3">
      <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-3">{title}</div>
      {children}
    </div>
  );
}

function HeroField({ name, value, onChange }) {
  const isLong = typeof value === "string" && (value.length > 90 || /^(sub|subtitle|body|description|desc)$/i.test(name));
  return (
    <div className={isLong ? "md:col-span-2" : ""}>
      <Label className="text-[11px] uppercase tracking-wider text-slate-500">{name}</Label>
      {isLong ? (
        <Textarea rows={2} value={value ?? ""} onChange={(e) => onChange(e.target.value)}/>
      ) : (
        <Input value={value ?? ""} onChange={(e) => onChange(e.target.value)}/>
      )}
    </div>
  );
}

function ObjectForm({ value, onChange }) {
  const keys = Object.keys(value || {});
  if (!keys.length) {
    return <p className="text-xs text-slate-500 italic">(empty)</p>;
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {keys.map((k) => {
        const v = value[k];
        const isArrayStr = Array.isArray(v) && (!v.length || typeof v[0] === "string");
        const isLong  = typeof v === "string" && (v.length > 90 || /^(body|description|desc|content|sub)$/i.test(k));
        const isNested = v && typeof v === "object" && !Array.isArray(v);
        return (
          <div key={k} className={isLong || isArrayStr || isNested ? "md:col-span-2" : ""}>
            <Label className="text-[11px] uppercase tracking-wider text-slate-500">{k}</Label>
            {isArrayStr ? (
              <Textarea rows={4} value={(v || []).join("\n")} onChange={(e) => onChange({ ...value, [k]: e.target.value.split(/\n/).filter((s) => s.trim().length) })} placeholder="One line per bullet"/>
            ) : isLong ? (
              <Textarea rows={3} value={v ?? ""} onChange={(e) => onChange({ ...value, [k]: e.target.value })}/>
            ) : isNested ? (
              <Textarea rows={4} className="font-mono text-xs" value={JSON.stringify(v, null, 2)}
                onChange={(e) => { try { onChange({ ...value, [k]: JSON.parse(e.target.value) }); } catch {} }}/>
            ) : typeof v === "number" ? (
              <Input type="number" value={v ?? 0} onChange={(e) => onChange({ ...value, [k]: Number(e.target.value) || 0 })}/>
            ) : (
              <Input value={v ?? ""} onChange={(e) => onChange({ ...value, [k]: e.target.value })}/>
            )}
          </div>
        );
      })}
    </div>
  );
}


function OriginalityPanel({ result, onClose }) {
  const score = result.score;
  const total = result.phrases_checked || 0;
  const dup = result.phrases_duplicated || 0;
  const matches = result.matches || [];

  let tone = "slate";
  let icon = null;
  let headline = "";
  if (score === null) {
    tone = "slate";
    headline = result.note || "Insufficient sample for a score.";
  } else if (score >= 85) {
    tone = "emerald"; icon = <ShieldCheck className="w-5 h-5 text-emerald-600"/>;
    headline = `${score}% original — looks unique.`;
  } else if (score >= 60) {
    tone = "amber"; icon = <AlertTriangle className="w-5 h-5 text-amber-600"/>;
    headline = `${score}% original — some phrases already indexed by Google.`;
  } else {
    tone = "red"; icon = <AlertTriangle className="w-5 h-5 text-red-600"/>;
    headline = `${score}% original — significant overlap detected. Consider rewriting flagged phrases.`;
  }

  const toneMap = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    amber:   "bg-amber-50 border-amber-200 text-amber-900",
    red:     "bg-red-50 border-red-200 text-red-900",
    slate:   "bg-slate-50 border-slate-200 text-slate-800",
  };

  return (
    <div className={`rounded-lg border p-4 mb-4 ${toneMap[tone]}`} data-testid="originality-result">
      <div className="flex items-start gap-3">
        {icon}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <div className="text-sm font-bold">{headline}</div>
            <button
              type="button" onClick={onClose}
              className="text-xs opacity-60 hover:opacity-100 flex-shrink-0" aria-label="Dismiss"
              data-testid="originality-dismiss"
            >✕</button>
          </div>
          {score !== null && (
            <div className="text-xs opacity-80 mt-0.5">
              {total - dup} of {total} sampled phrases were unique
              {result.checked_at && <> · checked {new Date(result.checked_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</>}
            </div>
          )}
          {matches.length > 0 && (
            <details className="mt-3">
              <summary className="text-xs font-semibold cursor-pointer hover:underline">
                Show {matches.length} flagged phrase{matches.length === 1 ? "" : "s"} & source URLs
              </summary>
              <ul className="mt-2 space-y-2">
                {matches.map((m, i) => (
                  <li key={i} className="rounded bg-white/60 border border-current/10 p-2">
                    <div className="text-xs italic mb-1">&ldquo;{m.phrase}&rdquo;</div>
                    <ul className="space-y-0.5">
                      {(m.hits || []).map((h, j) => (
                        <li key={j} className="text-xs flex items-start gap-1">
                          <ExternalLink className="w-3 h-3 mt-0.5 flex-shrink-0 opacity-60"/>
                          <a href={h.url} target="_blank" rel="noopener noreferrer" className="underline break-all hover:opacity-70">
                            {h.title || h.url}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      </div>
    </div>
  );
}
